import React from 'react';
import { Link } from '@/components/LocalizedLink';
import { ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { fadeUp } from '@/lib/motion';
import { useLanguage } from '@/hooks/useLanguage';

function HeroBadges({ labels }) {
  return (
    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.5 }} className="flex items-center gap-2 mb-6 flex-wrap">
      {labels.map(label => (
        <span key={label} className="text-[10px] uppercase tracking-[0.15em] text-saffron-light border border-saffron-light/30 px-3 py-1 rounded-full font-sans bg-saffron/5 backdrop-blur-sm">
          {label}
        </span>
      ))}
    </motion.div>
  );
}

function HeroStats({ stats }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 0.8 }} className="mt-16 flex flex-wrap gap-8">
      {stats.map((stat, i) => (
        <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 + i * 0.15, duration: 0.5 }}>
          <span className="font-display text-3xl font-bold text-saffron-light">{stat.value}</span>
          <p className="text-xs text-cream-muted/60 font-sans mt-1 uppercase tracking-wider">{stat.label}</p>
        </motion.div>
      ))}
    </motion.div>
  );
}

function HeroActions({ hero }) {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.5 }} className="mt-10 flex flex-wrap gap-4">
      <Link to="/contact" className="inline-flex items-center gap-2 bg-saffron text-white font-sans font-semibold text-sm px-8 py-4 rounded-md hover:bg-saffron-dark transition-colors shadow-saffron-glow" data-testid="hero-cta-quote">
        {hero.cta1} <ArrowRight size={16} />
      </Link>
      <Link to="/products" className="inline-flex items-center gap-2 border-2 border-white/30 text-white font-sans font-semibold text-sm px-8 py-4 rounded-md hover:bg-white/10 transition-colors backdrop-blur-sm" data-testid="hero-cta-products">
        {hero.cta2}
      </Link>
    </motion.div>
  );
}

export default function HeroSection({ heroImg }) {
  const { t } = useLanguage();
  const stats = [
    { value: '10+', label: t.stats.years },
    { value: '15+', label: t.stats.countries },
    { value: '500+', label: t.stats.export },
    { value: '200+', label: t.stats.buyers },
  ];
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0">
        <img src={heroImg} alt="Premium Indian spice powders from ASJK Laxmi Global - Turmeric, Red Chilli, Coriander, Garlic, Cumin and Black Pepper Powder" width={1264} height={848} fetchPriority="high" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-spice-deep/90 via-spice-deep/70 to-spice-deep/40" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 w-full">
        <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ duration: 0.7 }} className="max-w-2xl">
          <HeroBadges labels={[t.hero.badge1, t.hero.badge2, t.hero.badge3]} />
          <motion.h1 initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.8 }} className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[1.05] tracking-tight">
            {t.hero.heading1}
            <br />
            <span className="text-saffron-light">{t.hero.heading2}</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.6 }} className="mt-6 text-lg sm:text-xl text-cream-warm/80 font-sans max-w-lg leading-relaxed">
            {t.hero.subtext}
          </motion.p>
          <HeroActions hero={t.hero} />
          <HeroStats stats={stats} />
        </motion.div>
      </div>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
          <motion.div className="w-1.5 h-1.5 bg-saffron rounded-full" animate={{ y: [0, 12, 0] }} transition={{ duration: 1.5, repeat: Infinity }} />
        </div>
      </div>
    </section>
  );
}
